/**
 * sanitizeChatUrl — allowlist URL schemes for chat markdown links.
 *
 * Chat content (AI responses, visitor messages) is rendered into real
 * <a href> / <Link to> elements. A malicious or model-generated link can
 * carry a dangerous scheme (javascript:, data:, vbscript:) or a
 * protocol-relative URL (//evil.com) that the browser would execute.
 *
 * Allowed: https, http, mailto, tel, and internal app paths beginning "/".
 * Blocked (rendered as plain text, no anchor): everything else, including
 * protocol-relative "//", bare schemes like javascript/data/vbscript, and
 * bare domains without an explicit safe scheme.
 */
const SAFE_PROTOCOLS = new Set(['https:', 'http:', 'mailto:', 'tel:']);

export function sanitizeChatUrl(href) {
  if (!href || typeof href !== 'string') return null;
  // Strip ASCII control chars (0x00-0x1F, 0x7F) that browsers ignore and
  // which can disguise a scheme (e.g. "java\tscript:").
  const cleaned = href.replace(/[\x00-\x1f\x7f]/g, '').trim();
  if (!cleaned) return null;

  // Internal app path
  if (cleaned.startsWith('/')) {
    // Protocol-relative URL — block (browser treats //evil.com as https://evil.com)
    if (cleaned.startsWith('//')) return null;
    return cleaned;
  }

  // Explicit scheme?
  const schemeMatch = cleaned.match(/^([a-zA-Z][a-zA-Z0-9+.\-]*):/);
  if (schemeMatch) {
    const scheme = schemeMatch[1].toLowerCase();
    if (SAFE_PROTOCOLS.has(scheme + ':')) return cleaned;
    return null;
  }

  // No scheme and not a path — a bare domain or fragment. Refuse to guess
  // (prevents "example.com" being treated as a relative path or auto-linked).
  return null;
}